import { type ModelConfig, ModelConfigBaseShape } from "./model-config.schema";
import { RETIRED_EFFORT_LEVEL_FIELDS } from "./retired-effort-level-fields";

/**
 * The provider-specific tuning fields of `ModelConfigBaseShape`, DERIVED rather
 * than restated.
 *
 * ## What this exists to stop
 *
 * Every write path that carries a model selection used to spell these fields out
 * by name — a React `setValue` sequence, an object spread, a watched-fields
 * array, a second copy of the Zod shape. Nothing checked any list was complete,
 * so adding a dialect meant remembering every site by hand. Adding Kimi missed
 * four of them: the picker offered an effort level that every save discarded,
 * and an absent level is not neutral — the vendor applies its own default, which
 * for Kimi is the most expensive one. Two reviewers found it; no test did.
 *
 * ## Why a destructuring rest, and why by EXCLUSION
 *
 * The set is what is LEFT after removing the three fields that are not
 * provider-specific. That direction is the whole safety property: a new provider
 * field added to `ModelConfigBaseShape` joins this set automatically and reaches
 * every call site with no edit. An include-list would have to be updated by the
 * same person who is already forgetting seven other lists.
 *
 * A destructuring rest rather than a filtered `Object.keys` because the rest
 * keeps each field's precise Zod type, so a schema spreading `ProviderTuningShape`
 * infers exactly what spelling the fields out would infer. It also fails LOUDLY
 * in the one direction that matters: rename `temperature` in the base shape and
 * this destructure stops compiling, instead of quietly reclassifying it as
 * provider-specific.
 *
 * The residual failure is the harmless one — a genuinely shared field added
 * later (a second sampling knob, say) would be copied around as though it were
 * provider-specific until it is named in the destructure below. Nothing is
 * silently DROPPED either way, and dropping is the only direction that costs a
 * customer.
 */
const {
  modelName: _modelName,
  modelProvider: _modelProvider,
  temperature: _temperature,
  ...providerTuningShape
} = ModelConfigBaseShape;

/**
 * The Zod sub-shape carrying exactly the provider-tuning fields.
 *
 * Spread it into any schema that accepts a model selection, instead of restating
 * the `z.enum(...)` lines.
 */
export const ProviderTuningShape = providerTuningShape;

/** A key of `ModelConfigBaseShape` that tunes one provider's reasoning behaviour. */
export type ProviderTuningField = keyof typeof ProviderTuningShape;

/**
 * Every LIVE provider-tuning field name, for the sites that ITERATE rather than
 * spread — a form reset, a watched-fields subscription, a badge row, a copy.
 *
 * The retired level names are in {@link ProviderTuningShape} — so every contract
 * spreading it refuses them — and deliberately NOT here: an iterating site copies
 * whatever a source holds under these names, and a copied retired key would turn
 * the write it lands in into a 400.
 *
 * `Object.keys` is typed `string[]` for every object in TypeScript, because a
 * value may carry keys its type does not name. This one cannot: it is the rest
 * of a destructure over an object literal declared in this package.
 */
const RETIRED = new Set<string>(RETIRED_EFFORT_LEVEL_FIELDS);
export const PROVIDER_TUNING_FIELDS: readonly ProviderTuningField[] = (
  Object.keys(ProviderTuningShape) as ProviderTuningField[]
).filter((field) => !RETIRED.has(field));

/**
 * The provider-tuning fields a `Source` object actually declares, all optional.
 *
 * Generic over the source so an untyped form-values bag (`Record<string,
 * unknown>`) and a typed picker selection both keep their own value types
 * through the copy. Widening either to `unknown` here would push a cast onto
 * every call site, which is the shape this helper exists to remove.
 */
export type ProviderTuningOf<Source> = {
  [Field in ProviderTuningField & keyof Source]?: Source[Field];
};

/**
 * The provider-tuning half of a model selection, with the canonical value types.
 *
 * Extend this in any UI type that carries a model selection rather than
 * restating the field union — a restatement is free to narrow one field and
 * nothing reports the divergence except a cast at every read site.
 */
export type ProviderTuning = ProviderTuningOf<ModelConfig>;

/**
 * Read one key off an object whose type carries no index signature.
 *
 * An interface without an index signature is not assignable to `Record<string,
 * unknown>`, so a generic helper cannot index its own parameter without a cast.
 * `Reflect.get` is the typed way to ask; narrowing its `any` to `unknown` at
 * this single boundary keeps `any` out of every consumer.
 */
function readOwnField(source: object, field: string): unknown {
  if (!Object.prototype.hasOwnProperty.call(source, field)) return undefined;
  const value: unknown = Reflect.get(source, field);
  return value;
}

/**
 * Copy every provider-tuning field that `source` defines, and nothing else.
 *
 * Call this instead of listing the fields. A site that lists them is a site that
 * goes stale the next time a provider is added.
 *
 * A field whose value is `undefined` is OMITTED rather than written as an
 * explicit `undefined` key. That matters at a write boundary: the backend's
 * `mergeModelConfig` reads a present key as an instruction to set the stored
 * value, so spreading `{ thinkingDisplay: undefined }` into a PATCH body would
 * clear a field the user never touched. Use `clearedProviderTuning()` where
 * explicit presence IS the intent, such as resetting a form.
 */
export function pickProviderTuning<Source extends object>(
  source: Source | null | undefined
): ProviderTuningOf<Source> {
  const picked: Record<string, unknown> = {};

  if (source !== null && source !== undefined) {
    for (const field of PROVIDER_TUNING_FIELDS) {
      const value = readOwnField(source, field);
      if (value !== undefined) picked[field] = value;
    }
  }

  // The loop writes only keys from `PROVIDER_TUNING_FIELDS`, and every value it
  // writes came off `source` under that same key, so the mapped type describes
  // exactly what was built. TypeScript cannot follow a key-by-key build into a
  // mapped type; this is that limit and nothing else.
  return picked as ProviderTuningOf<Source>;
}

/** Every provider-tuning field, present and explicitly `undefined`. */
export type ClearedProviderTuning = { [Field in ProviderTuningField]: undefined };

/**
 * Every provider-tuning field set to `undefined`, for a form reset or a set of
 * default values where the key must EXIST to be registered and watched.
 *
 * Returns a fresh object per call: the result is spread into react-hook-form
 * default values, which the form library owns and may mutate.
 */
export function clearedProviderTuning(): ClearedProviderTuning {
  const cleared: Record<string, undefined> = {};

  for (const field of PROVIDER_TUNING_FIELDS) {
    cleared[field] = undefined;
  }

  // Same limit as `pickProviderTuning`: the loop covers exactly the field set
  // the mapped type names, and TypeScript cannot verify that from a loop.
  return cleared as ClearedProviderTuning;
}
